import React, { useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { Store } from 'react-notifications-component';
import { isNumber } from 'lodash';
import PropTypes from 'prop-types';

import Handle from '../Handle';
import ServicesDropdown from '../../../../../../../../../components/Dropdown/ServicesDropdown';
import Tooltip from '../../../../../../../../../components/Tooltip';

import {
  getErrorMessage,
  getOptionIcon,
  getProviderName,
  getStatusName,
  getServiceTypeName,
  getHighlightingText,
  getBoolRoundingRate,
  getRoundingRate,
  isDangerRate,
} from '../../../../../../helpers/getters';
import { getCategoriesList } from '../../../../../../../../../redux/actions/services';
import { openModal, changeServiceStatus, changeServiceStatusSuccess, changeServiceStatusError } from '../../../../../../reducer';
import { MODALS } from '../../../../../../constants';
import { servicesStatus } from '../../../../../../../../../services/endpoints/services';

const RowService = ({ style, service, search, searchOption, config, massActions, serviceDispatch, onChangeCheckbox }) => {
  const dispatch = useDispatch();

  const isActiveCategorySearch = searchOption === 'category-name';

  const [firstRender, setFirstRenter] = useState(true);
  useEffect(() => {
    const timer = setTimeout(() => {
      setFirstRenter(false);
    }, 350);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const statusName = useMemo(() => getStatusName(service.status), [service.status]);
  const visibility = service.visibility;
  const providerName = useMemo(() => getProviderName(service.provider_id, service.mode), [service.provider_id, service.mode]);
  const provider = service?.provider;
  const serviceType = useMemo(() => getServiceTypeName(service.type), [service.type]);
  const mode = Number(service.mode);

  const { options } = service;

  const onChangeStatus = async () => {
    const newServiceStatus = visibility ? 0 : 1;
    try {
      await serviceDispatch(changeServiceStatus());
      await servicesStatus(service.id, newServiceStatus);
      await dispatch(getCategoriesList());
      await serviceDispatch(changeServiceStatusSuccess(service.id));
      return true;
    } catch (error) {
      /*
        Если есть активный Subscription
        Не давать дизейдлить услугу
        Таска https://www.notion.so/perfectpanel/Services-subscriptions-53fc5d0c29be465688da06098e7c1216
       */
      if (error?.data?.error_type === 'modal') {
        await serviceDispatch(changeServiceStatusError());
        return serviceDispatch(openModal(MODALS.CHANGE_SERVICE_STATUS, error.data));
      }
      const errorMessage = getErrorMessage(error);
      await Store.addNotification({
        title: 'Error',
        message: errorMessage,
        type: 'danger',
        insert: 'top',
        container: 'bottom-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 3000,
        },
      });
      await serviceDispatch(changeServiceStatusError());
      return false;
    }
  };

  const onChangeAction = (selectedValue) => {
    const { value } = selectedValue;
    switch (value) {
    case MODALS.CHANGE_STATUS:
      return onChangeStatus();
    default:
      return serviceDispatch(openModal(value, service));
    }
  };

  const actionsList = service.actions;
  const { isDropdownDropUp, isActiveFilters } = service;

  const showProviderColumn = config?.access?.see_provider_column;
  const hideProvider = config?.access?.hide_provider;
  const showProviderServiceId = provider?.show_provider_service_id;

  const isCheckBox = massActions?.length;
  const isActionsList = actionsList?.length;

  const isChangePosition = config?.access?.edit_services;

  const isSameProviderCurrency = mode && service.provider && config.currency === service.provider?.currency;
  const isProviderCurrency = !!service?.provider?.currency;
  const isRateProvider = !!(service.rate && service.rate.provider);
  const isRateCustom = !!(service.rate && service.rate.custom);
  const isRateConverted = !!(service.rate && service.rate.converted);
  const isRateDanger = isDangerRate(service.rate, isSameProviderCurrency);

  const isMinCustom = service?.min.hasOwnProperty('custom') && isNumber(service.min.custom);
  const isMinProvider = service?.min.hasOwnProperty('provider') && isNumber(service.min.provider);
  const isMinSync = service.auto_min;

  const isMaxCustom = service?.max.hasOwnProperty('custom') && isNumber(service.max.custom);
  const isMaxProvider = service?.max.hasOwnProperty('provider') && isNumber(service.max.provider);
  const isMaxSync = service.auto_max;

  const isStatusSync = service.auto_status;

  const isProviderError = service?.provider_error;
  const isLongTypeName = service.isLongTypeName;

  return (
    <div className={classNames('service-table__body')} style={style} id={`service-${service.id}`}>
      <div
        className={classNames('service-table__row service-table__row-service', {
          'service-table__row-disabled': !visibility,
          'service-table__row-checked': service.checked,
          'service-table__row-highlight': service.highlight,
        })}
      >
        <div className="service-table__td service-table__checker">
          <div className="service-table__controls">
            {isChangePosition && ((!search && !isActiveFilters) || isActiveCategorySearch) ? <Handle /> : null}
            {isCheckBox ? (
              <input type="checkbox" checked={service.checked} onChange={(event) => onChangeCheckbox(event, service.id)} className="service-table__controls-checkbox" />
            ) : null}
          </div>
        </div>
        <div className="service-table__td service-table__id">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={getHighlightingText(service.id, search, (searchOption === 'service-id' || searchOption === null))} />
        </div>
        <div className="service-table__td service-table__service">
          {/* eslint-disable-next-line react/no-danger */}
          <span dangerouslySetInnerHTML={getHighlightingText(service.name, search, (searchOption === 'service-name' || searchOption === null))} />
        </div>
        <div className="service-table__td service-table__type">
          <div
            className={classNames({
              'service-table__type-long': !isLongTypeName,
            })}
          >
            {serviceType}
            {options.length ? (
              <div className="service-table__type-options">
                {options.map((option) => {
                  const icon = getOptionIcon(option);
                  if (icon) {
                    return (
                      <Tooltip key={option} placement="top" text={icon.tooltip} tooltipClass="tooltip__top">
                        <span className={classNames(icon.className, 'service-table__type-options-icon')} />
                      </Tooltip>
                    );
                  }
                  return null;
                })}
              </div>
            ) : null}
          </div>
        </div>
        {showProviderColumn ? (
          <div className="service-table__td service-table__provider">
            {providerName === 'error_provider' && !hideProvider ? (
              <>Manual</>
            ) : (
              <>{hideProvider && mode ? null : <div className="service-table__provider-name">{providerName}</div>}</>
            )}
            {service.provider_service_id && !hideProvider && showProviderServiceId ? (
              <div className="service-table__provider-service-id">
                <span dangerouslySetInnerHTML={getHighlightingText(service.provider_service_id, search, (searchOption === 'external-id' || searchOption === null))} />
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="service-table__td service-table__rate">
          {isRateCustom ? (
            <div
              className={classNames('service-table__rate-custom', {
                'service-table__td-sync-wrapper': service.auto_rate,
                'service-table__rate-danger': isRateDanger,
              })}
            >
              {service.auto_rate ? (
                <Tooltip
                  placement="top"
                  tooltipClass="tooltip__top"
                  text={service.sync_error ? (
                    <p>
                      Sync failed
                      <br />
                      {' '}
                      Last successful sync
                      <br />
                      {' '}
                      {service.last_success_sync || ''}
                    </p>
                  ) : 'Sync rate with provider'}
                >
                  <div className={classNames('service-table__td-sync-icon', {
                    'service-table__td-sync-icon-failed': service.sync_error,
                  })}
                  />
                </Tooltip>
              ) : null}
              <div>
                {getBoolRoundingRate(service.rate.custom) ? (
                  <span className="service-table__rate-rounding">
                    <Tooltip placement="left" tooltipClass="tooltip__left" text={service.rate.custom}>
                      <span>{getRoundingRate(service.rate.custom)}</span>
                    </Tooltip>
                  </span>
                ) : (
                  service.rate.custom
                )}
              </div>
            </div>
          ) : null}
          {isRateProvider || isRateConverted ? (
            <div className="service-table__rate-provider">
              {isRateConverted && !isSameProviderCurrency ? (
                <Tooltip
                  placement="left"
                  tooltipClass="tooltip__left"
                  text={`${service.rate.provider} ${isProviderCurrency ? service.provider.currency : ''}`}
                >
                  <span>
                    ≈
                    {' '}
                    {service.rate.converted}
                  </span>
                </Tooltip>
              ) : (
                <span>{isRateProvider ? service.rate.provider : null}</span>
              )}
              {isProviderCurrency && !isRateConverted && !isSameProviderCurrency ? (
                <span className="service-table__rate-provider-currency">{service.provider.currency}</span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="service-table__td service-table__min">
          <div
            className={classNames('service-table__min-custom', {
              'service-table__td-sync-wrapper': isMinSync,
            })}
          >
            {isMinSync ? (
              <Tooltip placement="top" text="Sync min with provider" tooltipClass="tooltip__top">
                <div className="service-table__td-sync-icon" />
              </Tooltip>
            ) : null}
            {isMinSync && (isMinProvider || isMinCustom) ? <span>{service.min.custom}</span> : <span>{service.min.custom}</span>}
          </div>
          {!isMinSync && isMinProvider ? <div className="service-table__min-provider">{service.min.provider}</div> : null}
        </div>
        <div className="service-table__td service-table__max">
          <div
            className={classNames('service-table__max-custom', {
              'service-table__td-sync-wrapper': isMaxSync,
            })}
          >
            {isMaxSync ? (
              <Tooltip placement="top" text="Sync max with provider" tooltipClass="tooltip__top">
                <div className="service-table__td-sync-icon" />
              </Tooltip>
            ) : null}
            {isMaxSync && (isMaxProvider || isMaxCustom) ? <span>{service.max.custom}</span> : <span>{service.max.custom}</span>}
          </div>
          {!isMaxSync && isMaxProvider ? <div className="service-table__max-provider">{service.max.provider}</div> : null}
        </div>
        <div className="service-table__td service-table__visibility">
          <div
            className={classNames('service-table__visibility-name', {
              'service-table__td-sync-wrapper': isStatusSync,
            })}
          >
            {isStatusSync ? (
              <Tooltip placement="top" text="Sync status with provider" tooltipClass="tooltip__top">
                <div className="service-table__td-sync-icon" />
              </Tooltip>
            ) : null}
            <span>{statusName}</span>
            {isProviderError ? (
              <Tooltip placement="top" text="Provider disabled this service" tooltipClass="tooltip__top">
                <span className="fas fa-exclamation-circle service-table__visibility-error-icon" />
              </Tooltip>
            ) : null}
          </div>
        </div>
        <div className="service-table__td service-table__action">
          {firstRender && isActionsList ? (
            <button
              className={classNames('font__weight-med dropdown-toggle btn btn-xs btn-default', {
                dropup: isDropdownDropUp,
              })}
            >
              Actions
              {' '}
              <span className="caret" />
            </button>
          ) : (
            <ServicesDropdown
              id={`service-action-${service.id}`}
              handleSelect={onChangeAction}
              bsSize="xsmall"
              title="Actions"
              items={actionsList}
              pullRight
              dropup={isDropdownDropUp}
            />
          )}
        </div>
      </div>
    </div>
  );
};

RowService.propTypes = {
  style: PropTypes.object,
  service: PropTypes.object,
  search: PropTypes.string,
  searchOption: PropTypes.string,
  config: PropTypes.object,
  massActions: PropTypes.array,
  serviceDispatch: PropTypes.func,
  onChangeCheckbox: PropTypes.func,
};

export default RowService;
